import React from "react";

import { Button, ButtonProps } from "@mui/material";
import { SxProps, Theme, useTheme } from "@mui/material/styles";
import BNBox from "../../BNBox/BNBox";

export interface BNButtonProps extends ButtonProps {
  target?: string;
  component?: any;
  to?: string;
  textDecoration?: boolean;
  noWrap?: boolean;
}

const BNButton = React.forwardRef<HTMLButtonElement, BNButtonProps>((props: BNButtonProps, ref) => {
  const theme = useTheme();
  const linkDecoration = useLinkDecorationSx();
  const {
    sx,
    size = "medium",
    variant = "contained",
    color = "primary",
    type = "button",
    textDecoration = true,
    noWrap,
    children,
    ...restOfProps
  } = props;

  const modifixedSx: SxProps<Theme> = [
    {
      borderRadius: variant === "text" && textDecoration ? 0 : "3rem",
      textTransform: "initial",
    },
    ResolveSxForSize(size),
    ResolveSxForVariant(variant, color),
    // You cannot spread `sx` directly because `SxProps` (typeof sx) can be an array.
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  let sxTextWrapper: SxProps<Theme> = noWrap
    ? {
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
      }
    : {};

  const isTextDecoration = variant === "text" && textDecoration;
  if (isTextDecoration) {
    sxTextWrapper = {
      ...sxTextWrapper,
      ...linkDecoration,
    };
  }

  return (
    <Button
      color={color}
      sx={modifixedSx}
      variant={variant}
      size={size}
      ref={ref}
      type={type}
      title={typeof children === "string" ? children : undefined}
      {...restOfProps}
    >
      {noWrap || isTextDecoration ? (
        <BNBox component={"span"} sx={sxTextWrapper}>
          {children}
        </BNBox>
      ) : (
        <>{children}</>
      )}
    </Button>
  );

  function ResolveSxForSize(size: BNButtonProps["size"]) {
    switch (size) {
      case "small":
        return {
          fontSize: "1.2rem",
        };
      case "medium":
        return {
          paddingTop: 1,
          paddingBottom: 1,
          paddingLeft: 3,
          paddingRight: 3,
          fontSize: "1.3rem",
        };
      case "large":
        return {
          [theme.breakpoints.up("md")]: {
            fontSize: "2rem",
            paddingLeft: "4rem",
            paddingRight: "4rem",
          },
        };
    }
    return {};
  }
  function ResolveSxForVariant(variant: BNButtonProps["variant"], color: BNButtonProps["color"]): SxProps<Theme> {
    switch (variant) {
      case "text":
        return {
          color: "text.primary",
          minWidth: "0rem",
          ":hover": {
            backgroundColor: "initial",
          },
        };
      case "outlined":
        return {
          color: "text.secondary",
          borderColor: theme.vars.palette.text.primary,
          "&:hover": {
            borderColor: theme.vars.palette.text.primary,
          },
        };
      case "contained":
        switch (color) {
          case "primary":
            return {};
          case "secondary":
            return {};
        }
        return {};
    }
    return {};
  }
});

export const useLinkDecorationSx = () => {
  const theme = useTheme();
  return {
    textDecoration: "underline",
    textDecorationColor: theme.vars.palette.primary.main,
    textDecorationThickness: "0.2rem",
    textUnderlineOffset: "0.4rem",
    "&:hover": {
      textDecorationColor: theme.vars.palette.primary.dark,
    },
    ...theme.applyStyles("dark", {
      textDecorationColor: theme.vars.palette.shadow.contrastText,
      textDecorationThickness: "0.1rem",
      "&:hover": {
        textDecorationColor: theme.vars.palette.shadow.contrastText,
      },
    }),
  } as SxProps<Theme>;
};

export default BNButton;
